import { motion } from "framer-motion";
import { ArrowRight, Crown, MapPin, Sparkles } from "lucide-react";
import { IMAGES, MISS_AURA_PROFILE } from "../../data/content";
import { reveal } from "../../lib/motion";

const CHAIN = ["Mulheres", "Profissionais", "Serviços", "Comunidade", "Propósito"];

export const Leadership = ({ onOpenRole }) => {
  return (
    <>
      {/* Seção 5 — Miss Aura Nº 1 */}
      <section
        data-testid="miss-aura-section"
        className="grain-overlay bg-[#141210] py-16 md:py-24 lg:py-32"
      >
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...reveal()} className="relative">
              <div className="gold-hairline absolute -top-6 left-1/2 w-2/3 -translate-x-1/2" />
              <div className="overflow-hidden rounded-3xl border border-gold/30">
                <img
                  src={IMAGES.missAura}
                  alt="Miss Vanessa Vieira — Miss Aura Nº 1, Fundadora do Ministério AURA"
                  data-testid="miss-aura-photo"
                  className="h-[420px] w-full object-cover sm:h-[540px]"
                />
              </div>
              <div className="animate-float-soft absolute -bottom-7 left-4 right-4 rounded-2xl border border-gold/40 bg-[#1A1816] px-7 py-4 text-center shadow-[0_20px_60px_rgba(197,160,89,0.18)] sm:left-1/2 sm:right-auto sm:w-max sm:-translate-x-1/2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">
                  {MISS_AURA_PROFILE.number}
                </p>
                <p className="mt-1 font-serif text-lg text-white">
                  {MISS_AURA_PROFILE.role}
                </p>
              </div>
            </motion.div>

            <div>
              <motion.p
                {...reveal()}
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-gold"
              >
                <Crown className="h-4 w-4" /> {MISS_AURA_PROFILE.number}
              </motion.p>
              <motion.h2
                {...reveal(0.1)}
                className="mt-4 font-serif text-3xl text-white sm:text-4xl lg:text-5xl"
              >
                Miss <em className="italic text-champagne">Vanessa Vieira.</em>
              </motion.h2>
              <motion.p
                {...reveal(0.15)}
                className="mt-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-gold/80"
              >
                {MISS_AURA_PROFILE.role}
              </motion.p>
              <motion.p
                {...reveal(0.2)}
                className="mt-2 text-sm text-champagne/90"
              >
                {MISS_AURA_PROFILE.titles}
              </motion.p>
              <motion.p
                {...reveal(0.25)}
                className="mt-6 border-l-2 border-gold pl-5 font-serif text-xl italic text-champagne sm:text-2xl"
              >
                "{MISS_AURA_PROFILE.quote}"
              </motion.p>
              <div className="mt-6 space-y-4">
                {MISS_AURA_PROFILE.bio.map((p, i) => (
                  <motion.p
                    key={i}
                    {...reveal(0.3 + i * 0.05)}
                    className="text-base leading-relaxed text-white/70"
                  >
                    {p}
                  </motion.p>
                ))}
              </div>
              <motion.p
                {...reveal(0.5)}
                className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50"
              >
                <MapPin className="h-3.5 w-3.5 text-gold/70" />
                {MISS_AURA_PROFILE.location}
              </motion.p>
              <motion.div {...reveal(0.55)} className="mt-8">
                <button
                  data-testid="miss-aura-cta"
                  onClick={() => onOpenRole("MISS_AURA")}
                  className="rounded-full bg-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-champagne sm:text-sm"
                >
                  Conhecer a Miss Aura
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção 6 — Embaixadoras */}
      <section
        data-testid="embaixadoras-section"
        className="bg-creamalt py-16 md:py-24 lg:py-32"
      >
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <motion.div {...reveal()} className="relative order-1 lg:order-2">
            <div className="overflow-hidden rounded-3xl border border-gold-border">
              <img
                src={IMAGES.community2}
                alt="Embaixadora AURA conectando mulheres"
                className="h-[380px] w-full object-cover sm:h-[480px]"
              />
            </div>
            <div className="animate-float-soft absolute -bottom-6 left-6 rounded-2xl border border-gold-border bg-white px-7 py-5 shadow-[0_20px_60px_rgba(197,160,89,0.12)]">
              <p className="font-serif text-xl text-ink">Liderança com propósito</p>
              <p className="mt-1 text-xs uppercase tracking-[0.15em] text-mutedink">
                5 a 10 Embaixadoras por Miss Aura
              </p>
            </div>
          </motion.div>

          <div className="order-2 lg:order-1">
            <motion.p
              {...reveal()}
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-gold"
            >
              <Sparkles className="h-4 w-4" /> As Embaixadoras
            </motion.p>
            <motion.h2
              {...reveal(0.1)}
              className="mt-4 font-serif text-3xl leading-tight text-ink sm:text-4xl lg:text-5xl"
            >
              "A mulher que abre a porta{" "}
              <em className="italic text-teal">para outras mulheres."</em>
            </motion.h2>
            <motion.p
              {...reveal(0.2)}
              className="mt-6 text-base leading-relaxed text-mutedink sm:text-lg"
            >
              As Embaixadoras AURA são líderes comunitárias que conectam mulheres
              a profissionais, serviços, comunidade e propósito. Elas são o
              coração pulsante da rede em cada cidade, cada igreja, cada bairro.
            </motion.p>
            <motion.div {...reveal(0.3)} className="mt-8 flex flex-wrap items-center gap-2">
              {CHAIN.map((item, i) => (
                <span key={item} className="flex items-center gap-2">
                  <span className="rounded-full bg-teal-light px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-teal-deep">
                    {item}
                  </span>
                  {i < CHAIN.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-gold" />}
                </span>
              ))}
            </motion.div>
            <motion.div {...reveal(0.4)} className="mt-10">
              <button
                data-testid="embaixadora-cta"
                onClick={() => onOpenRole("EMBAIXADORA")}
                className="rounded-full bg-teal px-9 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-teal-hover sm:text-sm"
              >
                Quero ser Embaixadora AURA
              </button>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};
